import {Injectable, inject} from "@angular/core";
import {environment} from "@env/environment";
import {BaseRestService} from "@app/services/base-rest.service";
import {catchError, Observable, throwError} from "rxjs";
import {HttpHeaders} from "@angular/common/http";
import {TranslateService} from "@ngx-translate/core";

@Injectable({
    providedIn: "root"
})

export class CollateralsService extends BaseRestService {
    private readonly translate = inject(TranslateService);
    readonly API_URL: string = environment.loanUri + '/collaterals';

    get(): any {
        return this.httpClient.get(this.API_URL,
            { headers: this.headers });
    }

    getByCustomerId(customerId: string, pageParams?: any, query?: string): Observable<any> {
        const options = {
            headers: this.headers,
            params: this.removeEmptyPropertyFromObject(pageParams)
        }
        return this.httpClient.get(this.API_URL + `/customers/${customerId}` + `?` + query, options);
    }

    create(data): Observable<any> {
        let headers = new HttpHeaders();
        headers.append("Content-Type", " multipart/form-data");
        return this.httpClient.post(this.API_URL, data,
            { headers: headers, responseType: 'text' });
    }

    getById(id: string): Observable<any> {
        const options = {
            headers: this.headers
        }
        return this.httpClient.get(this.API_URL + '/' + id, options);
    }

    update(data, id: string): Observable<any> {
        return this.httpClient.put(this.API_URL + '/' + id, data,
            { headers: this.headers, responseType: 'text' });
    }
    
    updateLinkedAccount(id: string, data): Observable<any> {
        return this.httpClient.put(this.API_URL + "/" + id + "/active", JSON.stringify(data),
            { headers: this.headers });
    }

    adjust(collateralId, data): any {
        return this.httpClient.post(this.API_URL + `/${collateralId}/adjust`, JSON.stringify(data),
            { headers: this.headers });
    }

    getAdjustmentInfo(id: string): Observable<any> {
        return this.httpClient.get(this.API_URL + `/${id}/adjustment/info`,  
            { headers: this.headers, });
    }

    makeRestitution(collateralId, data): any {
        return this.httpClient.post(this.API_URL + `/${collateralId}/make-restitution`, JSON.stringify(data),
            { headers: this.headers });
    }

    getRestitutionInfo(id: string): Observable<any> {
        return this.httpClient.get(this.API_URL + `/${id}/restitution/info`,  
            { headers: this.headers, });
    }

    disburse(collateralId, data): any {
        return this.httpClient.post(this.API_URL + `/${collateralId}/disburse`, JSON.stringify(data),
            { headers: this.headers });
    }

    getDisbursementInfo(id: string): Observable<any> {
        return this.httpClient.get(this.API_URL + `/${id}/disbursement/info`,  
            { headers: this.headers, });
    }

    collateralApprove(data): Observable<any> {
        return this.httpClient.put(this.API_URL + "/approve", JSON.stringify(data),
            { headers: this.headers }).pipe(
                catchError((error) => {
                    if (error?.error?.errorCode === "302025") {
                        error.error.message = this.translate.instant("collaterals.approve_no_linked_acc_for_currency");
                    }
                    return throwError(() => error);
                })
            );
    }

    collateralReject(data): Observable<any> {
        return this.httpClient.put(this.API_URL + "/reject", JSON.stringify(data),
            { headers: this.headers });
    }

    collateralSendForApproval(data): Observable<any> {
        return this.httpClient.put(this.API_URL + "/send/for-approval", JSON.stringify(data),
            { headers: this.headers });
    }

    collateralSendForRepairing(data): Observable<any> {
        return this.httpClient.put(this.API_URL + "/send/for-repairing", JSON.stringify(data),
            { headers: this.headers });
    }

    close(data): Observable<any> {
        return this.httpClient.put(this.API_URL + "/close", JSON.stringify(data),
            { headers: this.headers });
    }

    reopen(data): Observable<any> {
        return this.httpClient.put(this.API_URL + "/reopen", JSON.stringify(data),
            { headers: this.headers });
    }

    deleteFiles(data): Observable<any> {
        let headers = new HttpHeaders();
        headers.append("Content-Type", " multipart/form-data");
        return this.httpClient.delete(this.API_URL + '/file', {
            headers,
            body: data
        });
    }

    addFile(data, id): Observable<any> {
        let headers = new HttpHeaders();
        headers.append("Content-Type", " multipart/form-data");
        return this.httpClient.post(this.API_URL + '/file/' + id, data, {
            headers
        });
    }

    getCollateralsSummary(pageParams, data): Observable<any> {
        return this.httpClient.post(this.API_URL + "/summary", data,
            { headers: this.headers, params: this.removeEmptyPropertyFromObject(pageParams) });
    }

    getFileById(fileId: string): any {
        let headers = new HttpHeaders();
        headers.append("Content-Type", "application/octet-stream");
        return this.httpClient.get(this.API_URL + `/file/${fileId}`,
            { headers: this.headers, responseType: 'blob' });
    }

    getList(pageParams: any): Observable<any> {
        const options = {
          headers: this.headers,
          params: this.removeEmptyPropertyFromObject(pageParams)
        }
        return this.httpClient.get(this.API_URL, options);
    }

    getOperationSummary(pageParams, data): Observable<any> {
        return this.httpClient.post(this.API_URL + "/operation/summary", data,
            { headers: this.headers, params: this.removeEmptyPropertyFromObject(pageParams) });
    }

    deleteOperationSummary(data): Observable<any> {
        return this.httpClient.delete(this.API_URL + "/operations",
          { headers: this.headers, body: data });
    }
    
}
